import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSubscriptionDetails, cancelSubscription, getNotificationPrefs, updateNotificationPrefs, createSubscriptionCheckout, logoutUser } from "../lib/api";
import { useAuth } from "../App";
import { Horse, ArrowLeft, CreditCard, Bell, SignOut, WarningCircle, CheckCircle, XCircle, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SubscriptionManage() {
  const { user, setUser } = useAuth() || {};
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [prefs, setPrefs] = useState({ daily_tips_email: false });
  const [cancelling, setCancelling] = useState(false);
  const [resubbing, setResubbing] = useState(false);

  useEffect(() => {
    getSubscriptionDetails().then(setDetails).catch(console.error);
    getNotificationPrefs().then(setPrefs).catch(console.error);
  }, []);

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll lose access at the end of your billing period.")) return;
    setCancelling(true);
    try {
      await cancelSubscription();
      toast.success("Subscription cancelled");
      setDetails(d => ({ ...d, active: false, cancelled: true }));
    } catch { toast.error("Failed to cancel"); }
    setCancelling(false);
  };

  const handleResub = async () => {
    setResubbing(true);
    try {
      const res = await createSubscriptionCheckout(window.location.origin);
      if (res.url) window.location.href = res.url;
    } catch { toast.error("Failed to create checkout"); }
    setResubbing(false);
  };

  const handleToggleTips = async () => {
    const newVal = !prefs.daily_tips_email;
    try {
      await updateNotificationPrefs({ daily_tips_email: newVal });
      setPrefs(p => ({ ...p, daily_tips_email: newVal }));
      toast.success(newVal ? "Daily tips email enabled" : "Daily tips email disabled");
    } catch { toast.error("Failed to update"); }
  };

  const handleLogout = async () => {
    await logoutUser();
    if (setUser) setUser(null);
    window.location.href = "/";
  };

  if (!details) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><p className="text-zinc-500 font-mono text-sm">Loading...</p></div>;

  const isActive = details.active || details.role === "admin";
  const expiresDate = details.expires ? new Date(details.expires).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null;

  return (
    <div data-testid="subscription-manage" className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-zinc-800 px-4 md:px-8 py-3">
        <div className="flex items-center justify-between max-w-[800px] mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-40.png" alt="Racing Form Guide" className="w-6 h-6" />
            <span className="text-lg font-heading font-black text-white">Racing Form Guide</span>
          </Link>
          <button onClick={handleLogout} data-testid="manage-logout" className="text-zinc-500 hover:text-[#E12B00] text-xs font-mono flex items-center gap-1">
            <SignOut weight="bold" size={14} /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-[800px] mx-auto p-4 md:p-8">
        <Link to="/" className="inline-flex items-center gap-1 text-zinc-500 hover:text-white text-xs font-mono mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <h1 className="text-2xl font-black mb-6 text-white">Account & Subscription</h1>

        {/* Account Info */}
        <div className="border border-zinc-800 p-4 mb-4">
          <p className="mono-label text-zinc-500 mb-2">Account</p>
          <p className="font-semibold text-white">{details.name || details.email}</p>
          <p className="text-sm text-zinc-400">{details.email}</p>
          {details.role === "admin" && <span className="inline-block mt-1 bg-[#D4AF37] text-black text-[9px] font-mono px-2 py-0.5 uppercase font-bold">Admin</span>}
          {details.all_access && (
            <a href="https://rawcompanytech.com/members" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-black px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest hover:from-[#E5C040] hover:to-[#D4AF37] transition-all">
              <Crown weight="fill" size={12} /> RAW All-Access &#10003;
            </a>
          )}
        </div>

        {/* Subscription Status */}
        <div className={`border p-4 mb-4 ${isActive ? (details.all_access ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#008A27] bg-[#008A27]/10") : "border-[#E12B00] bg-[#E12B00]/10"}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="mono-label text-zinc-500">Subscription</p>
            {details.all_access ? (
              <a href="https://rawcompanytech.com/members" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-black px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest">
                <Crown weight="fill" size={12} /> RAW All-Access &#10003;
              </a>
            ) : isActive ? (
              <span data-testid="sub-active-badge" className="flex items-center gap-1 text-[#008A27] text-xs font-mono font-bold">
                <CheckCircle weight="fill" size={14} /> Active
              </span>
            ) : (
              <span data-testid="sub-inactive-badge" className="flex items-center gap-1 text-[#E12B00] text-xs font-mono font-bold">
                <XCircle weight="fill" size={14} /> Inactive
              </span>
            )}
          </div>
          {details.all_access ? (
            <>
              <p className="text-2xl font-black text-[#D4AF37]">All-Access Member</p>
              <p className="text-xs text-zinc-400 mt-1">All paid features unlocked via your RAW Company membership</p>
              <a href="https://rawcompanytech.com/members" target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-xs font-mono text-[#D4AF37] underline hover:text-white">Manage membership at rawcompanytech.com</a>
            </>
          ) : (
            <>
              <p className="text-2xl font-black">£9.99<span className="text-sm text-zinc-400 font-normal">/month</span></p>
              {expiresDate && isActive && <p className="text-xs text-zinc-500 mt-1">Renews: {expiresDate}</p>}
              {details.role === "admin" && <p className="text-xs text-[#008A27] mt-1">Admin accounts have complimentary access</p>}
            </>
          )}

          <div className="flex gap-2 mt-4">
            {isActive && details.role !== "admin" && !details.all_access && (
              <button
                data-testid="cancel-sub-btn"
                onClick={handleCancel}
                disabled={cancelling}
                className="border border-[#E12B00] text-[#E12B00] px-4 py-2 text-xs font-mono uppercase hover:bg-[#E12B00] hover:text-white transition-colors disabled:opacity-50"
              >
                {cancelling ? "Cancelling..." : "Cancel Subscription"}
              </button>
            )}
            {!isActive && (
              <button
                data-testid="resub-btn"
                onClick={handleResub}
                disabled={resubbing}
                className="bg-[#E12B00] text-white px-6 py-2 text-xs font-mono uppercase hover:bg-[#C52400] transition-colors disabled:opacity-50"
              >
                {resubbing ? "Redirecting..." : "Resubscribe - £9.99/month"}
              </button>
            )}
          </div>
        </div>

        {/* Email Notifications */}
        <div className="border border-zinc-800 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Bell weight="bold" size={16} className="text-[#D4AF37]" />
            <p className="mono-label text-zinc-500">Email Notifications</p>
          </div>
          <label className="flex items-center justify-between cursor-pointer group">
            <div>
              <p className="text-sm font-semibold text-white">Daily Tips Email</p>
              <p className="text-xs text-zinc-500">Receive our top 3 AI picks every morning before the first race</p>
            </div>
            <button
              data-testid="toggle-tips-email"
              onClick={handleToggleTips}
              className={`w-12 h-6 rounded-full relative transition-colors ${prefs.daily_tips_email ? "bg-[#008A27]" : "bg-zinc-300"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.daily_tips_email ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
          </label>
        </div>

        {/* Legal Links */}
        <div className="border border-zinc-800 p-4">
          <p className="mono-label text-zinc-500 mb-3">Legal</p>
          <div className="space-y-2">
            <Link to="/terms" className="block text-sm text-zinc-400 hover:text-[#D4AF37]">Terms of Service</Link>
            <Link to="/privacy" className="block text-sm text-zinc-400 hover:text-[#D4AF37]">Privacy Policy</Link>
            <Link to="/disclaimer" className="block text-sm text-zinc-400 hover:text-[#D4AF37]">Disclaimer</Link>
          </div>
        </div>

        <div className="mt-6 flex items-start gap-2 text-[10px] text-zinc-400 font-mono">
          <WarningCircle weight="fill" size={12} className="mt-0.5 flex-shrink-0" />
          <p>All odds are calculated by RAW Company Tech's AI algorithm and are NOT official bookmaker odds. For entertainment only. 18+</p>
        </div>
      </main>
    </div>
  );
}
